import { getModuleDefectDetailCached } from '@/utils/moduleDefectDetailCache';
import {
  moduleDefectMatchesPk,
  resolveDefectShapeGeomForOverlay
} from '@/utils/defect/defectShapeOverlay';
import { DefectDialogAccessMode, resolveDefectDialogAccessMode } from './defectDialogAccessMode';
import { shouldUseCellDefaultForDefect } from './defectCode.utils';
import { fetchDefectsByThermalImageId, fetchParentDefectTypes } from './defectType.repository';
import { enrichDefectSolarModuleDetail } from './enrichDefectSolarModuleDetail';

export { DefectDialogAccessMode, resolveDefectDialogAccessMode };

function getAiInclude(rootState) {
  return rootState.dashboard?.showAiDefectFlag || false;
}

async function ensureParentDefectTypes({ commit, dispatch, getters }) {
  if (getters.defectTypesLoaded) {
    return getters.parentDefectTypes;
  }
  return dispatch('loadParentDefectTypes');
}

async function loadDefectListForThermal({ commit, rootState }, thermalImage) {
  const aiInclude = getAiInclude(rootState);
  const defects = await fetchDefectsByThermalImageId(thermalImage.id, aiInclude);

  // [DEBUG] SET_DEFECT_LIST 직전 — defectStatus 값들 콘솔 출력
  if (defects.length > 0) {
    const statusSet = new Set(defects.map(d => d?.defectStatus)); 
      `[DEFECT-SESSION] loadDefectListForThermal → SET_DEFECT_LIST (n=${defects.length})`,
      'unique defectStatus values:',
      [...statusSet],
      'sample:',
      defects.slice(0, 3).map(d => ({ id: d.id ?? d.seq, status: d.defectStatus }))
    
  }

  commit('SET_DEFECT_LIST', defects);
  return { raw: defects, display: defects };
}

async function enrichDefectWithDetailShape(defect) {
  if (!defect?.id) {
    return defect;
  }
  let merged = defect;
  if (!resolveDefectShapeGeomForOverlay(merged)) {
    const detail = await getModuleDefectDetailCached(defect.id);
    if (detail && typeof detail === 'object') {
      merged = { ...defect, ...detail };
    }
  }
  const geom = resolveDefectShapeGeomForOverlay(merged);
  if (geom && !merged.defectShapeGeom) {
    return { ...merged, defectShapeGeom: geom };
  }
  return merged;
}

async function bootstrapEditDefect(storeCtx, { defect, defectList }) {
  const { commit, dispatch } = storeCtx;
  await ensureParentDefectTypes(storeCtx);

  const fromList = defectList?.find(d => d.id === defect.id) || defect;
  const withShape = await enrichDefectWithDetailShape(fromList);
  const resolved = await enrichDefectSolarModuleDetail(withShape);

  commit('SET_DEFECT', resolved);
  if (resolved?.solarModule) {
    commit('UPDATE_SOLAR_MODULE', resolved.solarModule);
  }

  if (shouldUseCellDefaultForDefect(resolved)) {
    await dispatch('setupDefectTypeInfoForCellDefault');
  } else if (resolved.defectCode) {
    await dispatch('setupDefectTypeInfo', resolved.defectCode);
  }

  await dispatch('defectForm/initializeFromServer', resolved, { root: true });
  commit('UPDATE_IS_EDIT', true);
  commit('SET_DEFECT_LIST', [resolved]);

  return resolved;
}

/**
 * 접근 모드별 다이얼로그 세션 데이터 로드 (RGB 제외).
 * @param {object} storeCtx commit, dispatch, rootState, getters
 */
export async function loadDefectDialogSession(
  storeCtx,
  { thermalImage, isDefectSelected, defect, showList }
) {
  const { commit, dispatch, rootState, getters } = storeCtx;
  const mode = resolveDefectDialogAccessMode({
    isDefectSelected,
    defect,
    showList
  });

  console.log('[DEFECT SESSION] load 시작:', { mode, thermalImageId: thermalImage?.id });

  switch (mode) {
    case DefectDialogAccessMode.VIEW_LIST:
    case DefectDialogAccessMode.NEW: {
      await loadDefectListForThermal({ commit, rootState }, thermalImage);
      await ensureParentDefectTypes({ commit, dispatch, getters });
      if (mode === DefectDialogAccessMode.NEW) {
        await dispatch('defectForm/resetDefectFormData', null, { root: true });
      }
      break;
    }

    case DefectDialogAccessMode.EDIT_FROM_MAP: {
      await bootstrapEditDefect(storeCtx, { defect });
      break;
    }

    case DefectDialogAccessMode.EDIT_FROM_LIST: {
      const aiInclude = getAiInclude(rootState);
      const defects = await fetchDefectsByThermalImageId(thermalImage.id, aiInclude);
      commit('SET_DEFECT_LIST', defects);
      let defectDetail = defects.find(d => moduleDefectMatchesPk(d, defect?.id));
      if (!defectDetail && defect?.id) {
        const fromApi = await getModuleDefectDetailCached(defect.id);
        if (fromApi && typeof fromApi === 'object') {
          defectDetail = fromApi;
        }
      }
      if (defectDetail) {
        await bootstrapEditDefect(storeCtx, {
          defect: defectDetail,
          defectList: defects
        });
      } else {
        console.warn('[DEFECT SESSION] 목록에서 결함을 찾지 못함:', defect?.id);
      }
      break;
    }

    default:
      console.warn('[DEFECT SESSION] 알 수 없는 모드:', mode);
  }

  console.log('[DEFECT SESSION] load 완료:', mode);
  return mode;
}
